import { apiGet, apiPost, unwrap } from './apiClient';
import { ResponseDTO, ApplicationDTO, PkgUploadResult, PaginationParams } from '@/types/api';

const BASE = '/api/v1/application/private';

export async function uploadPrivateApp(
  file: File,
  tenantId?: string
): Promise<PkgUploadResult> {
  // Defensive client-side validation. The backend returns a generic
  // "Failed to upsert application: null" HTTP 500 when an empty file is
  // posted, which is impossible to debug from the browser. Catch it here.
  if (!file) {
    throw new Error('No file selected. Please choose a .pkg file to upload.');
  }
  if (typeof file.size === 'number' && file.size === 0) {
    throw new Error(
      `The selected file "${file.name}" is empty (0 bytes). ` +
        'Please pick the original .pkg file from your local disk and try again.'
    );
  }

  const formData = new FormData();
  // Re-wrap to guarantee the underlying bytes are attached. Some browsers
  // (and sandboxed iframes) hand back File handles whose bytes can't be
  // streamed; reading first surfaces that error before the network call.
  formData.append('applicationFile', file, file.name);
  if (tenantId) {
    formData.append('tenantId', tenantId);
  }

  const res = await apiPost<ResponseDTO<PkgUploadResult>>(`${BASE}/upload`, formData);
  return unwrap(res);
}

export async function downloadPrivateApp(tenantId: string, appId: string): Promise<Response> {
  return apiGet<Response>(`${BASE}/download`, {
    params: { tenantId, appId },
    rawResponse: true,
  });
}

export async function getManifest(tenantId: string, appId: string): Promise<string> {
  const res = await apiGet<Response>(`${BASE}/manifest`, {
    params: { tenantId, appId },
    rawResponse: true,
  });
  return res.text();
}

export async function downloadIcon(tenantId: string, appId: string): Promise<Blob> {
  const res = await apiGet<Response>(`${BASE}/icon/download`, {
    params: { tenantId, appId },
    rawResponse: true,
  });
  return res.blob();
}

export async function listPrivateApps(pagination?: PaginationParams): Promise<ResponseDTO<ApplicationDTO[]>> {
  return apiGet<ResponseDTO<ApplicationDTO[]>>(`${BASE}/list`, {
    params: pagination as Record<string, string | number | boolean | undefined>,
    includeTenantHeader: true,
  });
}

export async function getPrivateApp(appId: string): Promise<ApplicationDTO> {
  const res = await apiGet<ResponseDTO<ApplicationDTO>>(BASE, {
    params: { appId },
    includeTenantHeader: true,
  });
  return unwrap(res);
}

export async function deletePrivateApp(appId: string): Promise<void> {
  await apiPost<ResponseDTO<unknown>>(`${BASE}/delete`, undefined, {
    params: { appId },
    includeTenantHeader: true,
  });
}
